
import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useCart } from '../../../context/CartContext';
import { shopClasses } from '../../../mocks/shopProducts';

export default function ShopClasses() {
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const { ref, isVisible } = useScrollAnimation();
  const { addToCart } = useCart();

  const handleAdd = (c: typeof shopClasses[0]) => {
    addToCart({
      id: `class-${c.id}`,
      name: c.title,
      price: c.price,
      image: c.image,
      type: 'class',
      description: c.description,
    });
  };

  return (
    <section id="shop-classes" className="py-24 bg-white scroll-mt-20">
      <div ref={ref} className="max-w-7xl mx-auto px-6">
        <div className={`flex flex-col lg:flex-row lg:items-end lg:justify-between mb-14 gap-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-300 flex items-center justify-center shadow-lg shadow-amber-500/20">
                <i className="ri-graduation-cap-line text-stone-900"></i>
              </div>
              <span className="text-amber-600 text-xs font-semibold tracking-[0.2em] uppercase">Learn From The Best</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-display font-normal text-stone-900">Cocktail Classes</h2>
          </div>
          <p className="text-stone-500 max-w-md leading-relaxed text-sm">
            From beginner fundamentals to advanced techniques, find the perfect class to elevate your mixology skills.
          </p>
        </div>

        <div className={`grid md:grid-cols-2 lg:grid-cols-3 gap-7 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-product-shop>
          {shopClasses.map((c) => (
            <div
              key={c.id}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-stone-100 hover:shadow-xl hover:border-amber-200/50 transition-all duration-500"
              onMouseEnter={() => setHoveredId(c.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <div className="relative h-48 overflow-hidden">
                <img src={c.image} alt={c.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 via-transparent to-transparent"></div>
                <div className="absolute top-3 left-3">
                  <span className="px-3 py-1 bg-gradient-to-r from-amber-400 to-yellow-300 text-stone-900 text-xs font-semibold rounded-full shadow-lg">{c.level}</span>
                </div>
                <div className="absolute top-3 right-3">
                  <span className="px-2.5 py-1 bg-white/90 backdrop-blur-sm text-stone-700 text-xs font-medium rounded-full">{c.spots} spots</span>
                </div>
                <div className="absolute bottom-3 left-3 flex items-center gap-1.5 text-white/90 text-sm">
                  <i className="ri-time-line"></i>
                  <span>{c.duration}</span>
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-lg font-serif font-semibold text-stone-900 mb-1.5">{c.title}</h3>
                <p className="text-stone-500 text-sm mb-4 line-clamp-2 leading-relaxed">{c.description}</p>

                <div className={`space-y-1.5 mb-4 overflow-hidden transition-all duration-500 ${hoveredId === c.id ? 'max-h-28 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider">Includes:</p>
                  {c.includes.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-1.5 text-sm text-stone-600">
                      <i className="ri-check-line text-amber-500 text-xs"></i>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                  <div>
                    <span className="text-2xl font-display text-stone-900">${c.price}</span>
                    <span className="text-stone-400 text-xs ml-1">/person</span>
                  </div>
                  <button
                    onClick={() => handleAdd(c)}
                    className="px-4 py-2 bg-gradient-to-r from-amber-400 to-yellow-300 text-stone-900 text-sm font-semibold rounded-full hover:from-amber-300 hover:to-yellow-200 transition-all cursor-pointer whitespace-nowrap shadow-md shadow-amber-500/20 hover:shadow-lg hover:scale-105 flex items-center gap-1.5"
                  >
                    <i className="ri-shopping-cart-line text-sm"></i>
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
